import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate } from 'react-router-dom'

import styled from 'styled-components'

import Spinner from 'components/Spinner'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'

import CustomerDetails from 'pages/Customer/CustomerDetails'
import DeviceTabs from 'pages/shared/DeviceTabs'
import NotFound from 'pages/NotFound'

import useCustomer from 'hooks/useCustomer'
import { useSessionContext } from 'hooks/SessionContext'
import useSearchParams from 'hooks/useSearchParams'

import { customerDisplayName } from 'helpers/formatters'

import { NoData, PageHeader, PageTitle, screenSize, SinglePanePage } from 'styles/shared'
import { color } from 'styles/colors'

const Customer = () => {
  const [refreshTimestamp, setRefreshTimestamp] = useState<number>(Date.now())

  const navigate = useNavigate()
  const { session: { dealerId } } = useSessionContext()
  const { customerId, deviceId } = useParams()
  const thermostatRthDate = useSearchParams('thermostatRthDate')
  const awairRthDate = useSearchParams('awairRthDate')
  const { customer, isCustomerLoaded } = useCustomer(dealerId, customerId, refreshTimestamp)
  const { pathname } = useLocation()
  const [selectedDeviceId, setSelectedDeviceId] = useState(deviceId)

  useEffect(() => {
    if (!deviceId || !customer) return

    setSelectedDeviceId((prevDeviceId) => {
      if (prevDeviceId === deviceId) return prevDeviceId
      return deviceId
    })

    const queryString = [
      thermostatRthDate && `thermostatRthDate=${thermostatRthDate}`,
      awairRthDate && `awairRthDate=${awairRthDate}`
    ].filter(Boolean).join('&')

    navigate(
      `/customers/${customerId}/systems/${getDeviceLink(deviceId)}${queryString ? `?${queryString}` : ''}`,
      { replace: true }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deviceId])

  const getDeviceLink = useCallback((deviceId: string) => {
    return `${deviceId}/dashboard`
  }, [])

  useEffect(() => {
    if (isCustomerLoaded) {
      if (customer?.devices?.length && !customer?.devices?.find((device) => device.deviceId === selectedDeviceId)) {
        // use replace instead of push to avoid back navigation issue. replace "/customers/customerId/" with "/customers/customerId/systems/deviceId"
        navigate(`/customers/${customerId}/systems/${getDeviceLink(customer.devices[0].deviceId)}`, { replace: true })
      } else if (!customer?.devices?.length && pathname !== '/customers/' + customerId) {
        navigate(`/customers/${customerId}`)
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.devices, customerId, navigate, isCustomerLoaded, pathname])

  const navigateToDeviceId = useCallback((deviceId: string) => {
    setSelectedDeviceId(deviceId)
    navigate(`/customers/${customerId}/systems/${getDeviceLink(deviceId)}`)
  }, [customerId, navigate, getDeviceLink])

  const refreshPage = () => {
    setRefreshTimestamp(Date.now())
  }

  const onSaveCallback = (deviceId: string) => {
    refreshPage()
    navigateToDeviceId(deviceId)
  }

  const device = customer?.devices?.find(device => device.deviceId === selectedDeviceId)
  const customerName = customerDisplayName(customer?.firstName, customer?.lastName, customer?.companyName)

  return (
    <S.Container>
      <S.PageHeader>
        <PageTitle>Customers</PageTitle>
        <S.BackButton onClick={() => navigate('/customers?view=all')}>
          <S.BackArrowIcon fontSize="small" />
          Back to Customers
        </S.BackButton>
      </S.PageHeader>

      {!isCustomerLoaded && <Spinner variant="page" />}

      {isCustomerLoaded && !customer && <NotFound />}
      {isCustomerLoaded && customer && (
      <S.SinglePanePage>
        <S.CustomerDetailsPanel>
          <S.CustomerDetails
            customer={customer}
            activeDevice={device}
            onSaveCallback={onSaveCallback}
            devices={customer.devices || []}
            refreshPageCallback={refreshPage}
          />
        </S.CustomerDetailsPanel>

        {selectedDeviceId &&
        <S.DeviceSection>
          <S.DeviceTabsPanel>
            <S.TabHeaders
              customerName={customerName}
              activeDeviceId={selectedDeviceId}
              devices={customer.devices || []}
              onActiveDeviceChangeCallback={navigateToDeviceId}
              refreshPageCallback={refreshPage}
              customerId={customer.id}
              renderMode="headersOnly"
            />
          </S.DeviceTabsPanel>
          <S.DashboardPanel>
            <S.TabContent
              customerName={customerName}
              activeDeviceId={selectedDeviceId}
              devices={customer.devices || []}
              onActiveDeviceChangeCallback={navigateToDeviceId}
              refreshPageCallback={refreshPage}
              customerId={customer.id}
              renderMode="contentOnly"
            />
          </S.DashboardPanel>
        </S.DeviceSection>
        }

        {!customer.devices?.length &&
        <NoData>
          <h1>NO SYSTEMS</h1>
          <h2>CLICK "ASSIGN SYSTEM" TO ASSIGN A SYSTEM TO THIS CUSTOMER.</h2>
        </NoData>
      }
      </S.SinglePanePage>
      )}
    </S.Container>

  )
}

export default Customer

const S = {
  Container: styled.div`
    width: 100%;
  `,
  PageHeader: styled(PageHeader)`
    @media ${screenSize.small} {
      display: none;
    }
  `,
  CustomerDetailsPanel: styled.div`
    background: ${color('paneBackground')};
    border: 1px solid ${color('border')};
    border-radius: 14px;
    padding: 16px 16px;
  `,
  DeviceSection: styled.div`
    background: ${color('dashboardBackground')};
    display: flex;
    flex-direction: column;
    border-radius: 14px;
  `,
  DeviceTabsPanel: styled.div`
    background: transparent;
  `,
  DashboardPanel: styled.div`    
    background: ${color('paneBackground')};
    border: 1px solid ${color('border')};    
    border-radius: 0 20px 20px 20px;
    overflow: hidden;
    margin-top: -1px;
  `,
  BackButton: styled.button`
    display: inline-flex;
    align-items: center;
    margin-left: -10px;
    color: ${color('anchor')};
    font-size: 14px;
    font-weight: 600;
    background: none;
    border: none;
    padding: 5px 10px;
    border-radius: 6px;
    cursor: pointer;
    &:hover {
      background-color: ${color('breadCrumbsBackgroundHover')};
    }
  `,
  BackArrowIcon: styled(ArrowBackIosIcon)`
    font-size: .75rem !important;
    margin-right: 10px;
  `,
  CustomerDetails: styled(CustomerDetails)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `,
  TabHeaders: styled(DeviceTabs)`
    & > div {
      background-color: transparent;
    }

    & .MuiTabs-root {
      background-color: ${color('dashboardBackground')};
    }

    & .MuiTab-root:not(.Mui-selected) {
      background-color: ${color('dashboardBackground')};
    }

    & .MuiTab-root.Mui-selected {
      background-color: ${color('paneBackground')};
    }
  `,
  TabContent: styled(DeviceTabs)`
    & > div {
      border-radius: 0;
      background-color: ${color('paneBackground')};
    }
  `,
  SinglePanePage: styled(SinglePanePage)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    background: transparent;
    border: none;
    padding: 0;

    @media ${screenSize.small} {
      margin-top: 72px;
    }
  `
}
